import React, { useState } from 'react';
import { difference, isEqual, union } from 'lodash';
import { Loader2 } from 'lucide-react';

import {
    DropdownMenuCheckboxItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
} from '../../../../shadcn/components/ui/DropdownMenu';

/**
 * Menu item that renders a selectable filter (single or multi-select) with optional local/remote search.
 *
 * @param {Object} props - Component props.
 * @param {string} [props.emptyRemoteSearchText='Type to search'] - Message shown before remote search when the query length is below the minimum; typically driven by the parent.
 * @param {boolean} [props.enableSearch=false] - Forces the search input to be shown instead of using automatic local search behavior.
 * @param {boolean} [props.hasError=false] - When true (and not loading), shows a generic error message in the options list.
 * @param {string|number} props.id - Stable identifier for the filter, used for element keys and label associations.
 * @param {boolean} [props.isLoading=false] - When true, shows a loading state in the options list.
 * @param {React.ReactNode} props.label - Label rendered in the dropdown trigger.
 * @param {string} [props.loadingText='Loading...'] - Text displayed while options are loading.
 * @param {boolean} [props.multi=false] - When true, renders a multi-select with checkboxes; otherwise a radio-group.
 * @param {(nextValue: string|number|Array<string|number>|undefined) => void} props.onChange - Called when the selection changes.
 * @param {(nextQuery: string) => void} [props.onQueryChange] - Called when the user types in the search box. When provided, the parent is responsible for filtering and/or fetching options.
 * @param {Array<{label?: string, value?: string|number}>|Array<string|number>} [props.options=[]] - Options to display (already fetched/filtered by the parent when remote search is used).
 * @param {string} [props.query] - Current search query; when omitted, the component manages its own local query state.
 * @param {boolean} [props.showRemotePrompt=false] - When true, shows `emptyRemoteSearchText` instead of any options (e.g., "type at least N characters").
 * @param {string|number|Array<string|number>} [props.value] - Current value; array when `multi` is true, scalar otherwise.
 * @returns {JSX.Element} Dropdown submenu containing the selectable filter.
 */
function SelectFilterMenuItem({
    emptyRemoteSearchText = 'Type to search',
    enableSearch = false,
    hasError = false,
    id,
    isLoading = false,
    label,
    loadingText = 'Loading...',
    multi = false,
    onChange,
    onQueryChange,
    options = [],
    query,
    showRemotePrompt = false,
    value,
}) {
    const autoLocalSearch = !enableSearch && Array.isArray(options) && options.length > 10;
    const isSearchEnabled = enableSearch || autoLocalSearch;

    // Query handling:
    // - When `query` + `onQueryChange` are provided, this component is fully controlled by the parent.
    // - Otherwise, it manages its own local query state and applies simple client-side filtering.
    const [internalQuery, setInternalQuery] = useState('');
    const isQueryControlled = typeof query === 'string' && typeof onQueryChange === 'function';
    const effectiveQuery = isQueryControlled ? query : internalQuery;
    const handleQueryChange = (next) => {
        if (isQueryControlled) {
            onQueryChange(next);
        } else {
            setInternalQuery(next);
        }
    };

    const baseOptions = Array.isArray(options) ? options : [];
    let visibleOptions = baseOptions;

    // Apply simple local filtering only when the parent is NOT controlling the query.
    if (!isQueryControlled && isSearchEnabled) {
        const q = (effectiveQuery || '').toLowerCase();
        if (q) {
            visibleOptions = baseOptions.filter((opt) => {
                const lbl = String(opt?.label ?? opt).toLowerCase();
                const val = String(opt?.value ?? opt).toLowerCase();
                return lbl.includes(q) || val.includes(q);
            });
        }
    }

    /**
     * Returns the human-readable label for a given option.
     *
     * Falls back to the stringified option when no explicit `label` field is present.
     *
     * @param {{ label?: string }|string|number} opt - Option object or primitive value.
     * @returns {string} Label to display in the menu.
     */
    const getOptionLabel = (opt) => opt.label ?? String(opt);

    /**
     * Returns the stable string value for a given option.
     *
     * Uses `opt.value` when available, otherwise stringifies the option itself.
     * This ensures consistent keys and comparison semantics for selection logic.
     *
     * @param {{ value?: string|number }|string|number} opt - Option object or primitive value.
     * @returns {string} Normalized string value for the option.
     */
    const getOptionValue = (opt) => String(opt.value ?? opt);

    if (multi) {
        const current = Array.isArray(value) ? value.map(String) : [];
        const visibleValues = (visibleOptions || []).map((opt) => getOptionValue(opt));
        const allSelected = visibleValues.length > 0 && visibleValues.every((v) => current.includes(v));
        return (
            <DropdownMenuSub>
                <DropdownMenuSubTrigger inset>{label}</DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-[280px] p-0">
                    <div className="max-h-72 overflow-y-auto">
                        {isSearchEnabled && (
                            <div className="sticky top-0 z-10 p-1 bg-white border-b border-gray-200">
                                <input
                                    type="text"
                                    value={effectiveQuery}
                                    onChange={(e) => handleQueryChange(e.target.value)}
                                    placeholder="Search..."
                                    className="w-full px-2 py-1 text-sm rounded border border-gray-200 outline-none"
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        )}
                        {showRemotePrompt && (
                            <div className="px-2 py-2 text-sm text-muted-foreground select-none">
                                {emptyRemoteSearchText}
                            </div>
                        )}
                        {!showRemotePrompt && isLoading && (
                            <div className="px-2 py-2 text-sm text-muted-foreground flex items-center gap-2 select-none">
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                <span>{loadingText}</span>
                            </div>
                        )}
                        {!showRemotePrompt && hasError && !isLoading && (
                            <div className="px-2 py-2 text-sm text-red-600 select-none">Failed to load options</div>
                        )}
                        <DropdownMenuCheckboxItem
                            key={`${id}-__all__`}
                            checked={allSelected}
                            disabled={visibleValues.length === 0 || isLoading || showRemotePrompt}
                            onSelect={(e) => {
                                // Keep dropdown open when selecting
                                e.preventDefault();
                            }}
                            onCheckedChange={(state) => {
                                const next = state ? union(current, visibleValues) : difference(current, visibleValues);
                                onChange(next);
                            }}
                        >
                            {allSelected ? 'Deselect all' : 'Select all'}
                        </DropdownMenuCheckboxItem>
                        {!showRemotePrompt &&
                            !isLoading &&
                            !hasError &&
                            (visibleOptions.length === 0 ? (
                                <div className="px-2 py-1 text-sm text-muted-foreground select-none">No results</div>
                            ) : (
                                (visibleOptions || []).map((opt) => {
                                    const v = getOptionValue(opt);
                                    const checked = current.includes(v);
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={`${id}-${v}`}
                                            checked={checked}
                                            onSelect={(e) => {
                                                // Keep dropdown open when selecting
                                                e.preventDefault();
                                            }}
                                            onCheckedChange={(state) => {
                                                const next = state ? union(current, [v]) : difference(current, [v]);
                                                onChange(next);
                                            }}
                                        >
                                            {getOptionLabel(opt)}
                                        </DropdownMenuCheckboxItem>
                                    );
                                })
                            ))}
                    </div>
                </DropdownMenuSubContent>
            </DropdownMenuSub>
        );
    }

    const current = value == null ? '' : String(value);
    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger inset>{label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-[200px] p-0">
                <div className="max-h-72 overflow-y-auto">
                    {isSearchEnabled && (
                        <div className="sticky top-0 z-10 p-1 bg-white border-b border-gray-200">
                            <input
                                type="text"
                                value={effectiveQuery}
                                onChange={(e) => handleQueryChange(e.target.value)}
                                placeholder="Search..."
                                className="w-full px-2 py-1 text-sm rounded border border-gray-200 outline-none"
                                onKeyDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}
                    {showRemotePrompt && (
                        <div className="px-2 py-2 text-sm text-muted-foreground select-none">
                            {emptyRemoteSearchText}
                        </div>
                    )}
                    {!showRemotePrompt && isLoading && (
                        <div className="px-2 py-2 text-sm text-muted-foreground flex items-center gap-2 select-none">
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            <span>{loadingText}</span>
                        </div>
                    )}
                    {!showRemotePrompt && hasError && !isLoading && (
                        <div className="px-2 py-2 text-sm text-red-600 select-none">Failed to load options</div>
                    )}
                    {!showRemotePrompt &&
                        !isLoading &&
                        !hasError &&
                        (visibleOptions.length === 0 ? (
                            <div className="px-2 py-1 text-sm text-muted-foreground select-none">No results</div>
                        ) : (
                            <DropdownMenuRadioGroup value={current}>
                                {(visibleOptions || []).map((opt) => {
                                    const v = getOptionValue(opt);
                                    return (
                                        <DropdownMenuRadioItem
                                            key={`${id}-${v}`}
                                            value={v}
                                            onSelect={(e) => {
                                                // Keep dropdown open and allow deselect when clicking the selected option
                                                e.preventDefault();
                                                if (isEqual(current, v)) onChange(undefined);
                                                else onChange(v);
                                            }}
                                        >
                                            {getOptionLabel(opt)}
                                        </DropdownMenuRadioItem>
                                    );
                                })}
                            </DropdownMenuRadioGroup>
                        ))}
                </div>
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
}

export { SelectFilterMenuItem };
